import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createOrganizacionComercial, updateOrganizacionComercial, getTiposOrganizacionComercial } from '../../api/client'
import { mensajeError, coincidenciasDuplicado, ordenarCatalogo } from './comercialLogica'
import { formularioDe, datosOrganizacion, hayCambios } from './organizacionesLogica'
import { fijarGuardia } from './useFichaComercial'
import ComercialAvisoDuplicados from './ComercialAvisoDuplicados'
import ComercialSelectorOrganizacion from './ComercialSelectorOrganizacion'
import ComercialSelectorPais from './ComercialSelectorPais'

const entrada = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200'

// An organization's form, drawn inside its side panel (edit and create modes,
// E5-2: no modal). Sends only what changed; a 409 posibles_duplicados shows the
// matches above the form, to review or save anyway. Editing, while it has
// unsaved changes, leaving the card (×, Esc, ‹, another card, cancel) or the page
// asks first; creating, leaving just drops what was typed (no question).
//   org: the card being edited (null = a new one); superiorInicial: a new one's superior
//   nombreInicial: a new one's trade name, already typed (created from a contact's form)
//   conGuardia: false when drawn inside another form that keeps its own (a contact's)
//   onGuardado(ficha): saved; onCancelar(): left without saving (already confirmed)
export default function ComercialOrganizacionForm({ org, superiorInicial, nombreInicial, conGuardia = true, onGuardado, onCancelar }) {
  const { t } = useTranslation('comercial')
  const inicial = useMemo(() => {
    const f = formularioDe(org, superiorInicial)
    return !org && nombreInicial ? { ...f, nombre_comercial: nombreInicial } : f
  }, [org, superiorInicial, nombreInicial])
  const [form, setForm] = useState(inicial)
  const [tipos, setTipos] = useState([])
  const [errorNombre, setErrorNombre] = useState('')
  const [error, setError] = useState('')
  const [coincidencias, setCoincidencias] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const avisoRef = useRef(null)

  const sucio = !!org && hayCambios(form, inicial)      // only an existing one guards its changes
  const sucioRef = useRef(false)
  useEffect(() => { sucioRef.current = sucio }, [sucio])
  useEffect(() => (conGuardia ? fijarGuardia(() => !sucioRef.current || window.confirm(t('form.descartar'))) : undefined),
    [t, conGuardia])
  useEffect(() => {
    if (!sucio) return undefined
    const avisar = (e) => { e.preventDefault(); e.returnValue = '' }      // reload or close the tab
    window.addEventListener('beforeunload', avisar)
    return () => window.removeEventListener('beforeunload', avisar)
  }, [sucio])

  useEffect(() => {
    getTiposOrganizacionComercial().then(d => setTipos(ordenarCatalogo(d))).catch(() => {})
  }, [])
  useEffect(() => { if (coincidencias) avisoRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) }, [coincidencias])

  const cambiar = (campo, valor) => {
    setForm(f => ({ ...f, [campo]: valor }))
    if (campo === 'nombre_comercial') setErrorNombre('')
    if (campo === 'nombre_comercial' || campo === 'razon_social') setCoincidencias(null)
  }
  const alternarTipo = (id) => cambiar('tipo_ids',
    form.tipo_ids.includes(id) ? form.tipo_ids.filter(x => x !== id) : [...form.tipo_ids, id])
  // Active types, plus an inactive one it already has (kept, marked; it can't be given anew).
  const tiposOfrecidos = tipos.filter(x => x.activo || inicial.tipo_ids.includes(x.id))

  const guardar = async (confirmar = false) => {
    if (!form.nombre_comercial.trim()) { setErrorNombre(t('form.nombreObligatorio')); return }
    const datos = datosOrganizacion(form, org ? inicial : null)
    if (org && !Object.keys(datos).length) { onCancelar(); return }
    if (confirmar) datos.confirmar_duplicados = true
    setGuardando(true)
    setError('')
    try {
      const ficha = org ? await updateOrganizacionComercial(org.id, datos) : await createOrganizacionComercial(datos)
      sucioRef.current = false
      onGuardado(ficha)
    } catch (err) {
      const c = coincidenciasDuplicado(err)
      if (c) setCoincidencias(c)
      else setError(mensajeError(err, t, t('form.fallo')))
      setGuardando(false)
    }
  }
  const cancelar = () => {
    if (sucioRef.current && !window.confirm(t('form.descartar'))) return
    sucioRef.current = false
    onCancelar()
  }

  return (
    <form noValidate onSubmit={e => { e.preventDefault(); guardar(false) }} className="flex flex-col min-h-full">
      <div className="space-y-5 flex-1">
        {coincidencias && (
          <div ref={avisoRef}>
            <ComercialAvisoDuplicados enLinea tipo="org" coincidencias={coincidencias} guardando={guardando}
              onAbrir={id => window.open(`/comercial/organizaciones?org=${id}`, '_blank', 'noopener')}
              onRevisar={() => setCoincidencias(null)} onGuardarIgual={() => guardar(true)} />
          </div>
        )}
        {org?.archivada && <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">{t('form.archivadaNota')}</p>}

        <Seccion titulo={t('ficha.identificacion')}>
          <Campo id="org-nombre" etiqueta={t('campos.nombre_comercial')} obligatorio error={errorNombre}>
            <input id="org-nombre" autoFocus={!org} value={form.nombre_comercial} onChange={e => cambiar('nombre_comercial', e.target.value)}
              aria-invalid={!!errorNombre} className={`${entrada} ${errorNombre ? 'border-red-300' : ''}`} />
          </Campo>
          <Campo id="org-razon" etiqueta={t('campos.razon_social')}>
            <input id="org-razon" value={form.razon_social} onChange={e => cambiar('razon_social', e.target.value)} className={entrada} />
          </Campo>
          <Campo id="org-identificador" etiqueta={t('campos.identificador_tributario')} ayuda={t('form.identificadorAyuda')}>
            <input id="org-identificador" value={form.identificador_tributario} onChange={e => cambiar('identificador_tributario', e.target.value)} className={entrada} />
          </Campo>
        </Seccion>

        <Seccion titulo={t('ficha.clasificacion')}>
          <Campo etiqueta={t('campos.tipos')}>
            {tiposOfrecidos.length
              ? <div role="group" aria-label={t('campos.tipos')} className="flex flex-wrap gap-1.5">
                  {tiposOfrecidos.map(x => {
                    const si = form.tipo_ids.includes(x.id)
                    return (
                      <button key={x.id} type="button" aria-pressed={si} onClick={() => alternarTipo(x.id)}
                        className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${si
                          ? 'bg-sky-600 text-white ring-sky-600' : 'bg-white text-gray-600 ring-gray-200 hover:ring-gray-400'}`}>
                        {si ? '✓ ' : ''}{x.nombre}{!x.activo ? ` (${t('chips.inactivo').toLowerCase()})` : ''}
                      </button>
                    )
                  })}
                </div>
              : <p className="text-xs text-gray-400">{t('form.sinTipos')}</p>}
          </Campo>
          <Campo id="org-superior" etiqueta={t('campos.superior')} ayuda={org?.archivada ? null : t('form.superiorAyuda')}>
            {org?.archivada
              ? <p className="text-sm text-gray-700">{org.superior?.nombre_comercial || t('ficha.sinSuperior')}</p>
              : <ComercialSelectorOrganizacion id="org-superior" value={form.superior_id} onChange={v => cambiar('superior_id', v)}
                  excluir={org} textoVacio={t('form.sinSuperior')} />}
          </Campo>
        </Seccion>

        <Seccion titulo={t('ficha.ubicacion')}>
          <Campo id="org-pais" etiqueta={t('campos.pais')}>
            <ComercialSelectorPais id="org-pais" value={form.pais} onChange={v => cambiar('pais', v)} />
          </Campo>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Campo id="org-ciudad" etiqueta={t('campos.ciudad')}>
              <input id="org-ciudad" value={form.ciudad} onChange={e => cambiar('ciudad', e.target.value)} className={entrada} />
            </Campo>
            <Campo id="org-sitio" etiqueta={t('campos.sitio_web')}>
              <input id="org-sitio" value={form.sitio_web} onChange={e => cambiar('sitio_web', e.target.value)} placeholder="www.ejemplo.cl"
                inputMode="url" className={entrada} />
            </Campo>
          </div>
          <Campo id="org-direccion" etiqueta={t('campos.direccion')}>
            <input id="org-direccion" value={form.direccion} onChange={e => cambiar('direccion', e.target.value)} className={entrada} />
          </Campo>
        </Seccion>

        <Seccion titulo={t('campos.descripcion')}>
          <textarea rows={4} value={form.observaciones} onChange={e => cambiar('observaciones', e.target.value)}
            className={`${entrada} resize-y`} aria-label={t('campos.descripcion')} />
        </Seccion>

        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 mt-6 px-4 sm:px-6 py-3 bg-white border-t border-gray-100 flex justify-end gap-2">
        <button type="button" onClick={cancelar}
          className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-700 hover:bg-gray-50">
          {t('catalogo.cancelar')}
        </button>
        <button type="submit" disabled={guardando}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40">
          {guardando ? t('form.guardando') : org ? t('form.guardarCambios') : t('form.crear')}
        </button>
      </div>
    </form>
  )
}

function Seccion({ titulo, children }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{titulo}</legend>
      {children}
    </fieldset>
  )
}

// A field: its label (tied to the control with id, when it has one), the
// control, and a help or error line.
function Campo({ id, etiqueta, obligatorio, ayuda, error, children }) {
  const Etiqueta = id ? 'label' : 'span'
  return (
    <div>
      <Etiqueta htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {etiqueta}{obligatorio && <span className="text-red-500"> *</span>}
      </Etiqueta>
      {children}
      {error ? <span className="block text-xs text-red-600 mt-1">{error}</span>
        : ayuda && <span className="block text-xs text-gray-400 mt-1">{ayuda}</span>}
    </div>
  )
}
